<script lang="ts">
  /* The layers panel.
   *
   * ═══ THE ONE PIECE DELIBERATELY WRITTEN TWICE, AND WHY ═══
   *
   * This used to say the dash's version could not be shared because it leans on
   * a stylesheet this app has not got. That stopped being true when the design
   * system moved into chart-ui — so here is the reason that actually holds.
   *
   * The dash's panel borrows a dozen GLOBAL utility classes (`card`,
   * `dockpanel`, `lbl`, `tbtn`, `mono`, `fs12`, `col`, `slider`) whose
   * appearance is then narrowed by CONTEXT-SENSITIVE overrides scattered
   * through a 900-line stylesheet — `.card .lbl` is a different size from
   * `.lbl`, and there are several such. Reproducing that inside a
   * self-contained component means reproducing rules I cannot see all of, on
   * the panel the boat opens most, to remove about 150 lines of PRESENTATION.
   *
   * And presentation is the cheap kind of duplication. Both panels ask the same
   * manager the same questions — `unavailableReason`, `isVisible`, `setOpacity`
   * — so they cannot disagree about a FACT. A tide height computed twice can be
   * wrong in one place; a checkbox laid out twice can only look different.
   *
   * Worth revisiting the day the dash's stylesheet is untangled, and not before.
   *
   * `unavailableReason` is the whole point of the panel on a hosted chart. Half
   * these layers cannot work without a marine-data provider, and a checkbox
   * that silently does nothing is worse than one that says why it is off.
   */
  import { layers } from '@gennetay/chart-core/map/layers.svelte';
  import { sources } from '@gennetay/chart-core/sources';
  import type { LayerCategory } from '@gennetay/chart-core/map/types';
  import Slider from '@gennetay/chart-ui/primitives/Slider';

  /* ═══ NO PROPS, AND THAT IS THE CHANGE ═══
   *
   * This carried `onclose` and `inSheet`: it drew its own header with its own
   * close button, its own bordered surface, its own bottom-sheet geometry at
   * `62dvh`, its own sidebar geometry in landscape, and its own scroll box —
   * about 150 lines of CSS describing WHERE it was rather than what it says.
   *
   * `<Panel>` supplies all of that in one place for every panel, so what is
   * left here is the list. The `62dvh` sheet, the `min(340px, 100vw - 16px)`
   * sidebar and the three media queries that switched between them are gone
   * with it. */

  /* ═══ ITS OWN TICK, AND ONLY WHILE IT IS OPEN ═══
   *
   * A module's `description` is a GETTER over a store, and a getter is not a
   * reactive dependency — so a row rendered while a field was still loading
   * kept saying "waiting for the surface temperature" with the wash drawn on
   * the chart behind it. Seen in a screenshot; every assertion passed.
   *
   * The chart's own sweep would do, but it is in a different component tree and
   * threading a pulse up through the app to reach here is more wiring than a
   * panel deserves. This component is MOUNTED ONLY WHILE OPEN, so its timer
   * exists exactly as long as something is looking at it. */
  let tick = $state(0);
  $effect(() => {
    const t = setInterval(() => tick++, 1000);
    return () => clearInterval(t);
  });

  /* ═══ A CATEGORY MISSING FROM HERE IS A LAYER THAT VANISHES ═══
   *
   * The panel renders section by section, so a registered layer whose category
   * is not named below is silently dropped — no row, no reason, no error. That
   * is exactly what happened to ActiveCaptain: it declares `traffic`, this list
   * did not have `traffic`, and a working layer with 55 anchorages loaded had
   * no way to be switched on except the bar's glyph.
   *
   * `traffic` is titled NOTES here rather than "TRAFFIC & NOTES" as it is on the
   * boat, because this host has no AIS and a heading promising traffic over a
   * list of anchorages is a heading that lies. `mine` is still absent, and
   * correctly — those are the boat's own marks and this deployment registers
   * none. */
  const SECTIONS: [LayerCategory, string][] = [
    ['charts', 'CHARTS'],
    ['tides', 'TIDES & CURRENTS'],
    ['weather', 'WEATHER'],
    ['fishing', 'FISHING'],
    ['traffic', 'NOTES']
  ];

  /* ═══ RE-READ ON THE PULSE ═══
   *
   * A module's `description` is a GETTER over a store, and a getter is not a
   * reactive dependency — so a row rendered while a field was still loading
   * kept saying "waiting for the surface temperature" with the wash drawn on
   * the chart behind it. Seen in a screenshot; every assertion passed.
   *
   * `void tick` re-derives once a second, which is the same rate everything
   * else on this chart moves at and cheap for a dozen rows. */
  const rows = $derived(
    (void tick,
    layers.list().filter((m) => {
      if (m.listed === false) return false;
      /* Provider-impossible rows do not belong on this host. Coverage-limited
       * rows remain listed and grey with a reason from unavailableReason. */
      if (m.requires?.some((source) => !sources().has(source))) return false;
      return !m.parent || layers.isVisible(m.parent);
    }))
  );
</script>

<div class="rows">
    {#each SECTIONS as [cat, title] (cat)}
      {@const inCat = rows.filter((m) => m.category === cat)}
      {#if inCat.length}
        <!-- #129/#130: real headings, not plain divs — every visual group
             heading must be exposed in the accessibility tree, with the
             checkboxes nested under it. `.grp` pins font + margin, so the
             visual is byte-identical. -->
        <h3 class="grp">{title}</h3>
        {#each inCat as m (m.id)}
          {@const reason = layers.unavailableReason(m.id)}
          {@const off = reason !== null}
          <div class="row" class:off>
            <label>
              <input
                type="checkbox"
                aria-label={`${m.title} — ${reason ?? m.description ?? ''}`}
                checked={layers.isVisible(m.id)}
                disabled={m.pinned || off}
                onchange={(e) => void layers.setVisible(m.id, e.currentTarget.checked)}
              />
              <span class="txt">
                <span class="name">{m.title}</span>
                <span class="sub">{reason ?? m.description ?? ''}</span>
              </span>
            </label>
            {#if m.supportsOpacity && !off && layers.isVisible(m.id)}
              <div class="opwrap">
                <span class="oplabel">{m.title} opacity</span>
                <Slider
                  min={0.1}
                  max={1}
                  step={0.05}
                  value={layers.state[m.id]?.opacity ?? 1}
                  label={`${m.title} opacity`}
                  oninput={(v) => layers.setOpacity(m.id, v)}
                />
              </div>
            {/if}
          </div>
        {/each}
      {/if}
    {/each}
</div>

<style>
  .rows {
    display: flex;
    flex-direction: column;
  }

  .grp {
    font: var(--fs-label) / 1 var(--px);
    letter-spacing: var(--tr-label);
    color: var(--sub);
    text-transform: uppercase;
    margin: var(--sp-6) 0 var(--sp-3);
  }
  .grp:first-child {
    margin-top: 0;
  }

  .row {
    padding: var(--sp-3) var(--sp-4);
    border-radius: var(--r-2);
  }
  .row:hover {
    background: var(--s-2);
  }
  .row.off {
    opacity: 0.5;
  }
  .row.off:hover {
    background: none;
  }

  label {
    display: flex;
    align-items: flex-start;
    gap: var(--sp-5);
    cursor: pointer;
  }
  .row.off label {
    cursor: default;
  }
  .txt {
    display: flex;
    flex-direction: column;
    gap: 1px;
    min-width: 0;
  }
  .name {
    /* Deliberate: titles render in the data/instrument voice (Chivo Mono).
       See docs/TYPE.md — the #60 decision blesses this, not drift. */
    font: var(--fs-body) / var(--lh-ui) var(--mono);
    color: var(--ink);
    letter-spacing: var(--tr-ui);
  }
  /* The reason a row is off, or what the layer is. Every word remains
     reachable; the panel scrolls, so hiding prose buys nothing. */
  .sub {
    font: var(--fs-read) / var(--lh-read) var(--sans);
    color: var(--sub);
    white-space: normal;
    overflow-wrap: anywhere;
  }
  /* Amber, because amber is this app's word for "not now" and an unavailable
     layer is exactly that — give the deployment a provider and it lights up
     with no code change. */
  .row.off .sub {
    color: var(--am);
  }
  .opwrap {
    display: flex;
    flex-direction: column;
    gap: var(--sp-2);
    width: 100%;
    margin-top: var(--sp-2);
  }
  .oplabel {
    font: var(--fs-label) / var(--lh-ui) var(--sans);
    color: var(--sub);
  }

  /* ═══ SIZED FOR THE FINGER, NOT FOR THE PANEL'S SHAPE ═══
   *
   * ONE query, keyed on the POINTER. There were three — a `(max-width: 640px)`
   * block for the sheet, a `(max-height: 500px) and (min-width: 641px)` block
   * for landscape, and a pointer block between them — and they had drifted: the
   * landscape one had grown its own copy of the label rules and had missed the
   * opacity slider, so on a phone held sideways the sliders stayed 16 px tall
   * with the fix sitting three rules above looking correct.
   *
   * The panel's SHAPE is a question about the screen and belongs to the shell.
   * The size of a target is a question about what is touching it, and that is
   * all that is left here. */
  @media (pointer: coarse) {
    /* THE WHOLE ROW IS THE TARGET, not the 13 px checkbox. `align-items:
       center` rather than flex-start, so a one-line row does not put the box
       above the text it belongs to. */
    label {
      min-height: var(--h-touch);
      align-items: center;
    }
    label input[type='checkbox'] {
      width: 22px;
      height: 22px;
      flex: none;
    }
  }
</style>
